// Context source projection: the role and the human-facing producer name
// of one logged non-user `user/message`, read from its durable `source` alone.
// The client keeps no table of known plugin ids — a renamed or newly mounted
// producer must never need a client release to stay identifiable, and a resumed
// or foreign log must project the same way as a live one.
/** One durable source narrowed to the readable-record shape; null for anything else. */
function asRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : null;
}
/** A record field read as a non-empty string, or null. */
function readString(record, key) {
    const value = record[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
}
/** Distinct non-empty `field` values of an array-valued source member, in first-seen order. */
function collect(source, member, field) {
    const list = source[member];
    if (!Array.isArray(list))
        return [];
    const seen = [];
    for (const entry of list) {
        const record = asRecord(entry);
        const value = record === null ? null : readString(record, field);
        if (value !== null && !seen.includes(value))
            seen.push(value);
    }
    return seen;
}
/** A collected name list rendered as one label; null when the list is empty. */
function joined(names) {
    return names.length > 0 ? names.join(', ') : null;
}
/**
 * The referenced-session labels of one durable `session-reference` recall
 * source, in first-seen order; empty for every other source shape, including
 * a foreign or older log whose reference entries carry no readable label.
 * @param source - the logged `user/message` source, exactly as recorded.
 * @returns distinct non-empty reference labels.
 */
export function sessionRecallLabels(source) {
    const record = asRecord(source);
    if (record === null || readString(record, 'kind') !== 'session-reference')
        return [];
    return collect(record, 'references', 'label');
}
/**
 * Project one durable message source onto its transcript role and producer name.
 *
 * The source arrives over the wire as opaque JSON (`MessageSource` is
 * merge-extensible, so no client-side union can be exhaustive), and a durable
 * log may predate or postdate this UI; every unreadable shape therefore
 * degrades to `inject` with whatever name the record still carries.
 * @param source - the logged `user/message` source, exactly as recorded.
 * @returns the role and producer name to present for this context.
 */
export function contextProvenance(source) {
    const record = asRecord(source);
    const kind = record === null ? null : readString(record, 'kind');
    if (record === null || kind === null)
        return { role: 'inject', label: null };
    switch (kind) {
        // Cross-session snapshots are the one durable source that carries another
        // session's material; its references name the sessions they were read from.
        case 'session-reference':
            return { role: 'recall', label: joined(collect(record, 'references', 'label')) ?? kind };
        // Workspace instructions name the files they were reconciled from, which
        // identifies the producer far better than the plugin id would.
        case 'agent-instructions':
            return { role: 'inject', label: joined(collect(record, 'changes', 'path')) ?? kind };
        case 'plugin':
            return { role: 'inject', label: readString(record, 'plugin') ?? kind };
        // A user-explicit skill invocation names the skill it injected.
        case 'skill-invocation':
            return { role: 'inject', label: readString(record, 'name') ?? kind };
        // Canonical Team participant speech (B2.1): a worker's accepted/result/
        // blocker message is a real conversation PARTICIPANT — never owner, never
        // assistant, never a generic context injection. Authorship comes from the
        // durable sender metadata alone (never a body-text prefix).
        case 'team-message': {
            const senderId = readString(record, 'senderId');
            const senderRole = readString(record, 'senderRole');
            return {
                role: 'participant',
                label: readString(record, 'senderName') ?? senderId ?? kind,
                ...(senderId === null ? {} : { senderId }),
                ...(senderRole === null ? {} : { senderRole }),
            };
        }
        case 'subagent-report':
            return { role: 'inject', label: readString(record, 'senderName') ?? 'Report' };
        case 'subagent-settled':
            return { role: 'inject', label: readString(record, 'senderName') ?? 'Result' };
        // Documented default arm of the merge-extensible source map: an unknown
        // producer still identifies itself by its own durable kind.
        default:
            return { role: 'inject', label: kind };
    }
}
/**
 * Whether a durable message source is INTERNAL-ONLY and must never render as an
 * owner-visible chat row in normal Team UX. A `subagent-settled` artifact is a
 * runtime settlement record, not a worker message; the worker's real contribution
 * reaches the owner through the shared Team Chat milestones and handoff. This is
 * the presentation boundary that keeps the internal "Result" injection label out
 * of the normal conversation.
 * @param source - the logged message source.
 * @returns true for internal-only sources.
 */
/**
 * Durable message sources that are INTERNAL Team/worker relay or settlement
 * artifacts. They stay MODEL-VISIBLE (the durable event is unchanged and still
 * reaches the model), but in normal owner mode they must NOT render as an
 * owner-visible context row — the owner sees the safe team-activity milestone
 * instead. A developer affordance (`__TEX7_DEBUG__`/`?inspect-tools`) may
 * re-enable inspection.
 *
 * Step 9.2.6R-B2.1: `team-message` was REMOVED here — canonical worker speech
 * is now a real conversation participant (see {@link contextProvenance}), not
 * an internal-only artifact. Other internal sources are unchanged.
 */
const INTERNAL_CONTEXT_SOURCES = new Set([
    'subagent-settled',
    'subagent-report',
]);
/** Whether the explicit developer inspection affordance is enabled. */
function inspectionEnabled() {
    if (typeof window === 'undefined')
        return false;
    const globals = window;
    if (globals.__TEX7_DEBUG__ === true)
        return true;
    const search = window.location?.search ?? '';
    return /(?:^[?#]|&)(?:inspect-tools|inspect-subagents)(?:=|&|$)/.test(search);
}
/**
 * Whether a durable message source is INTERNAL-ONLY and must not render as an
 * owner-visible chat row in normal Team UX (model-visible, presentation-hidden).
 * The durable event is never removed from model context; this only governs the
 * human transcript. A developer may inspect them behind the explicit affordance.
 * @param source - the logged message source.
 * @returns true for internal-only sources in normal mode.
 */
export function isInternalOnlyContextSource(source) {
    if (inspectionEnabled())
        return false;
    const record = asRecord(source);
    return record !== null && INTERNAL_CONTEXT_SOURCES.has(readString(record, 'kind') ?? '');
}
/** Context forms this UI version renders with a dedicated presentation. The
 * durable vocabulary (`ContextForm` in `llm`) may already be wider — an
 * unrecognized or absent value degrades to the opaque presentation rather than
 * dropping the row, so a log written by a newer or foreign producer still
 * renders.
 */
const KNOWN_FORMS = ['instructions', 'catalog', 'snapshot', 'notice', 'relay', 'recall'];
/**
 * Read the producer-declared form off one durable message source.
 * @param source - the logged `user/message` source, exactly as recorded.
 * @returns the form when this UI version presents it, otherwise null (opaque).
 */
export function contextForm(source) {
    const record = asRecord(source);
    const form = record === null ? null : readString(record, 'form');
    return form !== null && KNOWN_FORMS.includes(form)
        ? form
        : null;
}
//# sourceMappingURL=context-provenance.js.map